/**
 * Phase 6.5 / C6 — WebGPU panorama (pre-connect menu background).
 *
 * Mirrors the WebGL `panorama.ts` BoxGeometry approach: 6 textured
 * faces of a 1000³ inverted box around the camera, rotating slowly.
 * No fade-in for the first cut; images snap visible as they load.
 *
 * Materials are per-face `MeshBasicNodeMaterial` instances so each face
 * can carry its own texture (BatchedMesh / multi-material array
 * supports this in r170+ — Mesh accepts a Material[] aligned to the
 * BoxGeometry's 6 groups).
 *
 * Texture orientation: the panorama webp set is mastered for an
 * inside-out box. We apply a 2x3 UV flip on each texture's matrix to
 * mirror across the U axis (same as the WebGL backend).
 */

import {
  Scene,
  PerspectiveCamera,
  BoxGeometry,
  Mesh,
  TextureLoader,
  ClampToEdgeWrapping,
  LinearFilter,
  MeshBasicNodeMaterial,
  DoubleSide,
  Texture,
} from 'three/webgpu'

const FILES = [
  'panorama_3.webp', // right (+x)
  'panorama_1.webp', // left (-x)
  'panorama_4.webp', // top (+y)
  'panorama_5.webp', // bottom (-y)
  'panorama_0.webp', // front (+z)
  'panorama_2.webp', // back (-z)
] as const

export class WebGPUPanorama {
  private mesh: Mesh<BoxGeometry, MeshBasicNodeMaterial[]> | null = null
  private time = 0
  private materials: MeshBasicNodeMaterial[] = []
  private textures: Texture[] = []
  private disposed = false

  constructor (private readonly scene: Scene, _camera: PerspectiveCamera) {
    const geom = new BoxGeometry(1000, 1000, 1000)
    // Pre-fill 6 placeholder materials so the Mesh can be added immediately.
    // Each face's texture is plugged in asynchronously below.
    for (let i = 0; i < 6; i++) {
      const m = new MeshBasicNodeMaterial({
        transparent: true,
        side: DoubleSide,
        depthWrite: false,
        opacity: 0,
      })
      this.materials.push(m)
    }
    this.mesh = new Mesh(geom, this.materials)
    this.scene.add(this.mesh)

    const loader = new TextureLoader()
    for (let i = 0; i < FILES.length; i++) {
      loader.load(
        `background/${FILES[i]}`,
        (tex) => {
          if (this.disposed) { tex.dispose(); return }
          tex.matrixAutoUpdate = false
          tex.matrix.set(-1, 0, 1, 0, 1, 0, 0, 0, 1)
          tex.wrapS = ClampToEdgeWrapping
          tex.wrapT = ClampToEdgeWrapping
          tex.minFilter = LinearFilter
          tex.magFilter = LinearFilter
          this.textures.push(tex)
          this.materials[i].map = tex
          this.materials[i].opacity = 1
          this.materials[i].needsUpdate = true
        },
      )
    }
  }

  /** Call from the animation loop. */
  tick () {
    if (!this.mesh || this.disposed) return
    this.time += 0.01
    this.mesh.rotation.y = Math.PI + this.time * 0.01
    this.mesh.rotation.z = Math.sin(-this.time * 0.001) * 0.001
  }

  dispose () {
    this.disposed = true
    if (this.mesh) {
      this.scene.remove(this.mesh)
      this.mesh.geometry.dispose()
      this.mesh = null
    }
    for (const m of this.materials) m.dispose()
    for (const t of this.textures) t.dispose()
    this.materials.length = 0
    this.textures.length = 0
  }
}
